import { defineStore } from 'pinia';
import { useCustomerStore } from './customer';
import { useInitializeStore } from './initialize';
import _ from 'lodash';

export const useCasinoStoreV2 = defineStore('casinoStoreV2', {
	state: () => ({
		selected_category_slug: null,
		selected_provider_slugs: [],

		search_term: '',
		search_mode: 'global',

		// Settings
		clear_filters_on_search: false,
		clear_search_on_category_switch: false,
		show_clear_all_badge: false,
		show_only_providers_with_games: false,
		show_only_categories_with_games: false,
		back_button_game_pages: 'back',
		back_button_game_iframe: 'landing',

		// Layout Settings
		default_aspect_ratio: 'square',
		default_tile_width: 150,
		default_scroll_rows: 1,

		// Category Icon Settings
		show_category_icons: false,
		category_icon_fallback: 'none',

		// Show All Button Settings
		show_all_button_position: 'bottom',

		// Mobile Grid Settings
		mobile_grid_columns: 3,
		mobile_grid_rows: 2,
		mobile_games_per_category: 15,

		// Game Info Settings
		show_game_info: true,


		// Custom page
		custom_page_slug: null,
		page_name: null,
		show_all_games_button: true,
		show_favourites: true,
		show_last_played_game: true,
		show_categories_slider: true,
		show_hero_banner: true,
		show_providers_slider: true,
		show_search_bar: true,
		show_grouping_switcher: true,
		default_grouping: 'category',
		landing_page_type: null,

		sort_type: 'popular',
		current_page: 1,
		total_pages: 0,
		total_games: 0,
		current_route: null,
		active_page: 'landing',

		categories: [],
		providers: [],
		games: [],

		collections: {},

		has_favourites: false,
		has_last_played: false,

		landing_games_per_page: null,
		general_games_per_page: null,

		is_loading: false,
		is_loading_more: false,
		redirect_to_landing: false,
		cancel_token_source: null,
	}),
	getters: {
		getIsLoading(state) {
			return state.is_loading;
		},
		getIsLoadingMore(state) {
			return state.is_loading_more;
		},
		getSearchTerm(state) {
			return state.search_term;
		},
		getSearchMode(state) {
			return state.search_mode;
		},
		getHasFavourites(state) {
			return state.has_favourites;
		},
		getHasLastPlayed(state) {
			return state.has_last_played;
		},
		getCategoryById: (state) => (category_id) => {
			return state.categories.find(c => String(c.id) === String(category_id)) || {};
		},
		getCategoryName: (state) => (category_id, locale = 'en') => {
			const category = state.categories.find(c => String(c.id) === String(category_id));

			if (!category) return '';

			let translations = {};
			if (category.translations) {
				try {
					translations = typeof category.translations === 'string' ? JSON.parse(category.translations) : category.translations;
				} catch { 0. }
			}

			return translations[locale] || category.name || '';
		},
		getCategoryTablesCount: (state) => (category_id) => {
			const category = state.categories.find(c => String(c.id) === String(category_id));

			return category ? category.games_count || 0 : 0;
		},
		getCategoryGamesCount: (state) => (category_id) => {
			const category = state.categories.find(c => String(c.id) === String(category_id));

			return category ? category.games_count || 0 : 0;
		},
		getCategorySlug: (state) => (category_id) => {
			const category = state.categories.find(c => String(c.id) === String(category_id));

			return category ? category.slug || '' : '';
		},
		getCategoryIcon: (state) => (category_id) => {
			if (!state.show_category_icons) return null;
			const category = state.categories.find(c => String(c.id) === String(category_id));
			if (category?.image) return category.image;
			if (state.category_icon_fallback === 'default') return '/assets/images/category-default-icon.svg';
			return null;
		},
		getShowCategoryIcons(state) {
			return state.show_category_icons;
		},
		getShowAllButtonPosition(state) {
			return state.show_all_button_position;
		},
		getCategoryId: (state) => {
			const category = state.categories.find(c => c.slug === state.selected_category_slug);

			return category ? category.id || null : null;
		},
		getProviderById: (state) => (provider_id) => {
			return state.providers.find(p => String(p.id) === String(provider_id)) || {};
		},
		getProviderName: (state) => (provider_id) => {
			const provider = state.providers.find(p => String(p.id) === String(provider_id));

			return provider ? provider.name || '' : '';
		},
		getProviderNameBySlug: (state) => (provider_slug) => {
			const provider = state.providers.find(p => String(p.slug) === String(provider_slug));

			return provider ? provider.name || '' : '';
		},
		getProviderSlug: (state) => (provider_id) => {
			const provider = state.providers.find(p => String(p.id) === String(provider_id));

			return provider ? provider.slug || '' : '';
		},
		getProviderGamesCount: (state) => (provider_id) => {
			const provider = state.providers.find(p => String(p.id) === String(provider_id));

			return provider ? provider.games_count || 0 : 0;
		},
		getProviderSlugs: (state) => {
			return state.selected_provider_slugs;
		},
		getGames: (state) => {
			return state.games;
		},
		getCategories(state) {
			return _.orderBy(state.categories, ['order', 'name'], ['asc', 'asc']);
		},
		getGamesCountPerCategory(state) {
			return state.games_count_per_category;
		},
		getSelectedCategorySlug(state) {
			return state.selected_category_slug;
		},
		getProviders(state) {
			return _.orderBy(state.providers, ['order', 'name'], ['asc', 'asc']);
		},
		getProvidersSorted: (state) => (sort_type = 'order') => {
			if (sort_type === 'alphabetical') {
				// Sort by name
				return _.orderBy(state.providers, ['name'], ['asc']);
			} else {
				// Default: sort by order, then by name
				return _.orderBy(state.providers, ['order', 'name'], ['asc', 'asc']);
			}
		},
		getActivePage(state) {
			return state.active_page;
		},
		getSortType(state) {
			return state.sort_type;
		},
		getSnapshotGamesPerProvider(state) {
			return state.landing_games_per_page;
		},
		getTotalGames(state) {
			return state.total_games;
		},
		hasNextPage(state) {
			return state.current_page < state.total_pages;
		},
		getGamesPerPage(state) {
			if (state.active_page === 'landing' && !state.custom_page_slug) {
				return state.landing_games_per_page;
			}

			return state.general_games_per_page;
		},
		getRedirectToLanding(state) {
			return state.redirect_to_landing;
		},
		getClearFiltersOnSearch(state) {
			return state.clear_filters_on_search;
		},
		getClearSearchOnCategorySwitch(state) {
			return state.clear_search_on_category_switch;
		},
		getShowClearAllBadge(state) {
			return state.show_clear_all_badge;
		},
		getShowOnlyProvidersWithGames(state) {
			return state.show_only_providers_with_games;
		},
		getShowOnlyCategoriesWithGames(state) {
			return state.show_only_categories_with_games;
		},
		getGameCollection: (state) => (collection_slug = 'featured_games') => {
			if (!state.collections[collection_slug]) {
				return [];
			}

			return _.orderBy(state.collections[collection_slug], ['is_favourite', 'favourite_timestamp', 'order', 'name'], ['desc', 'desc', 'asc', 'asc']);
		},
		getStorageUrl() {
			if (!process.env.VUE_APP_STORAGE_URL) return '';

			return process.env.VUE_APP_STORAGE_URL;
		},
		getCustomPageSlug(state) {
			return state.custom_page_slug;
		},
		getPageName(state) {
			return state.page_name;
		},
		getShowAllCategoriesButton(state) {
			return state.show_all_games_button;
		},
		getShowFavourites(state) {
			return state.show_favourites;
		},
		getShowLastPlayedGame(state) {
			return state.show_last_played_game;
		},
		getShowCategoriesSlider(state) {
			return state.show_categories_slider;
		},
		getShowHeroBanner(state) {
			return state.show_hero_banner;
		},
		getShowProvidersSlider(state) {
			return state.show_providers_slider;
		},
		getShowSearchBar(state) {
			return state.show_search_bar;
		},
		getShowGroupingSwitcher(state) {
			// Don't show grouping switcher if it's a custom page
			if (state.custom_page_slug) {
				return false;
			}
			return state.show_grouping_switcher;
		},
		getDefaultGrouping(state) {
			return state.default_grouping;
		},
		getLandingPageType(state) {
			return state.landing_page_type;
		},
		getShowLeftSlider(state) {
			if (state.show_search_bar) {
				return true;
			}

			// If show_categories_slider is true and at least one of the others is true, return true, otherwise false
			if (state.show_categories_slider &&	(state.show_all_games_button || state.show_favourites || state.show_last_played_game)) {
				return true;
			}

			return false;
		}
	},
	actions: {
		fetchGames() {
			if (this.current_page == 1) this.is_loading = true;
			else this.is_loading_more = true;

			// Check and cancel the previous request
			if (this.cancel_token_source) {
				this.cancel_token_source.cancel('New category selected');
			}

			// Create a new cancel token
			this.cancel_token_source = global.axios.CancelToken.source();

			let device = 'desktop';

			if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
				device = 'mobile';
			}

			let payload = {
				device: device,
				category_slug: this.selected_category_slug,
				search_term: this.search_term,
				page: this.current_page,
				per_page: this.getGamesPerPage,
				sort_type: this.sort_type,
			};

			if (this.active_page === 'landing') {
				payload['landing_page'] = this.landing_page_type;
			}

			if (this.selected_provider_slugs.length) {
				payload['provider_slugs'] = this.selected_provider_slugs;
			}

			if (this.active_page === 'favourites') {
				payload['favourites_only'] = true;
			} else if (this.active_page === 'last_played_games') {
				payload['last_played_only'] = true;
			}

			if (this.custom_page_slug) {
				payload['page_slug'] = this.custom_page_slug;
			}

			return global.axios.post(process.env.VUE_APP_API_URL + '/casino', payload, { headers: { 'Version': 'v2' }, cancelToken: this.cancel_token_source.token }).then((response) => {

				if (response.data['redirect_to_landing']) {
					this.redirect_to_landing = true;

					return Promise.resolve(response.data);
				}

				// Search Settings
				this.search_mode = response.data['settings']['search_mode'];
				this.clear_filters_on_search = response.data['settings']['clear_filters_on_search'] || false;
				this.clear_search_on_category_switch = response.data['settings']['clear_search_on_category_switch'] || false;
				this.show_clear_all_badge = response.data['settings']['show_clear_all_badge'] || false;
				this.show_only_providers_with_games = response.data['settings']['show_only_providers_with_games'] || false;
				this.show_only_categories_with_games = response.data['settings']['show_only_categories_with_games'] || false;
				this.back_button_game_pages = response.data['settings']['back_button_game_pages'] || 'back';
				this.back_button_game_iframe = response.data['settings']['back_button_game_iframe'] || 'landing';

				// Layout Settings
				this.default_aspect_ratio = response.data['settings']['default_aspect_ratio'] || 'square';
				this.default_tile_width = parseInt(response.data['settings']['default_tile_width']) || 150;
				this.default_scroll_rows = parseInt(response.data['settings']['default_scroll_rows']) || 1;

				// Category Icon Settings
				this.show_category_icons = response.data['settings']['show_category_icons'] || false;
				this.category_icon_fallback = response.data['settings']['category_icon_fallback'] || 'none';

				// Show All Button Settings
				this.show_all_button_position = response.data['settings']['show_all_button_position'] || 'bottom';

				// Mobile Grid Settings
				this.mobile_grid_columns = parseInt(response.data['settings']['mobile_grid_columns']) || 3;
				this.mobile_grid_rows = parseInt(response.data['settings']['mobile_grid_rows']) || 2;
				this.mobile_games_per_category = parseInt(response.data['settings']['mobile_games_per_category']) || 15;

				// Game Info Settings
				this.show_game_info = response.data['settings']['show_game_info'] ?? true;

				// Recalculate games-per-page now that settings are loaded from API
				this.setGamesPerPage(window.innerWidth);

				// Custom Page Settings
				this.page_name = response.data['page_name'] ?? null;
				this.show_all_games_button = response.data['settings']['show_all_games_button'] ?? true;
				this.show_favourites = response.data['settings']['show_favourites'] ?? true;
				this.show_last_played_game = response.data['settings']['show_last_played_game'] ?? true;
				this.show_categories_slider = response.data['settings']['show_categories_slider'] ?? true;
				this.show_hero_banner = response.data['settings']['show_hero_banner'] ?? true;
				this.show_providers_slider = response.data['settings']['show_providers_slider'] ?? true;
				this.show_search_bar = response.data['settings']['show_search_bar'] ?? true;
				this.show_grouping_switcher = response.data['settings']['show_grouping_switcher'] ?? true;
				this.default_grouping = response.data['settings']['default_grouping'] ?? 'category';
				this.landing_page_type = response.data['settings']['landing_page_type'] ?? this.landing_page_type;

				this.categories = response.data['categories'];
				this.providers = response.data['providers'];
				if (this.current_page == 1) {
					this.games = response.data['data']['data'];
				} else {
					if (this.active_page == 'landing' && !this.custom_page_slug) this.games = { ...this.games, ...response.data['data']['data'] };
					else this.games = [...this.games, ...response.data['data']['data']];
				}
				this.total_pages = response.data['data']['total_pages'];
				this.total_games = response.data['data']['total'];
				this.current_page = response.data['data']['page'];

				this.has_favourites = response.data['has_favourites'] || false;
				this.has_last_played = response.data['has_last_played'] || false;


				if (this.current_page == 1) this.is_loading = false;
				else this.is_loading_more = false;

				return Promise.resolve(response.data.data);
			}).catch((error) => {
				if (this.current_page == 1) this.is_loading = false;
				else this.is_loading_more = false;

				if (global.axios.isCancel(error)) {
					console.log('Fetch cancelled:', error.message);
				} else {
					console.error('An error occurred:', error.response ? JSON.stringify(error.response.data) : (error.message || error));
					return Promise.reject(error);
				}
			});
		},
		fetchGameCollection(collection_slug) {
			let initializeStore = useInitializeStore();

			let device = initializeStore.isMobile == true ? 'mobile' : 'desktop'

			let payload = {
				device: device,
				collection_slug: collection_slug
			};

			return global.axios.post(process.env.VUE_APP_API_URL + '/casino/collection', payload, { headers: { 'Version': 'v2' } }).then((response) => {
				this.collections[collection_slug] = response.data.data;

				return Promise.resolve(response.data.data);
			}).catch((error) => {
				return  Promise.resolve(error);
			});
		},
		setSearchTerm(current_route, search_term) {
			this.search_term = search_term;
		},
		setSortType(sort_type) {
			this.sort_type = sort_type;
			this.current_page = 1;
		},
		setActivePage(active_page) {
			this.active_page = active_page;
		},
		setCurrentRoute(current_route) {
			this.current_route = current_route;
		},
		setCasinoPageParams(current_route, category_slug, provider_slugs, custom_page_slug = null) {
			// console.log('casinoStore->setCasinoPageParams: Params: ', current_route, category_slug, provider_slugs, custom_page_slug);
			this.current_route = current_route;
			this.selected_category_slug = category_slug || null;
			this.custom_page_slug = custom_page_slug || null;

			this.selected_provider_slugs = provider_slugs
				? provider_slugs.split(',').map(slug => slug.trim()).filter(Boolean)
				: [];

			// Early return for global search
			if (this.search_mode === 'global' && this.search_term) {
				// console.log('casinoStore->setCasinoPageParams: Search mode is global, setting active page to search');
				this.active_page = 'search';
				this.fetchGames();
				return;
			}

			// Route to active_page mapping
			const route_to_page = {
				'casino': 'landing',
				'custom-casino': 'landing',
				'casino-category': 'category',
				'custom-casino-category': 'category',
				'casino-providers': 'provider',
				'custom-casino-providers': 'provider',
				'casino-category-provider': 'category',
				'custom-casino-category-provider': 'category',
				'casino-favourites': 'favourites',
				'custom-casino-favourites': 'favourites',
				'casino-favourites-provider': 'favourites',
				'custom-casino-favourites-provider': 'favourites',
				'casino-last-played': 'last_played_games',
				'custom-casino-last-played': 'last_played_games',
				'casino-last-played-provider': 'last_played_games',
				'custom-casino-last-played-provider': 'last_played_games',
			};

			this.active_page = route_to_page[current_route] || 'landing';
			// console.log('casinoStore : Active page set to:', this.active_page);

			this.fetchGames();
		},

		loadMoreGames() {
			if (this.is_loading || !this.hasNextPage) return;

			this.current_page++;

			this.fetchGames();
		},
		setGamesPerPage(width) {
			// Landing page
			const MOBILE_BREAKPOINT = 768;
			const GAP = 10;

			if (width <= MOBILE_BREAKPOINT) {
				// Mobile: use mobile_games_per_category setting (enough for horizontal scroll)
				this.landing_games_per_page = this.mobile_games_per_category || 15;
			} else {
				// Desktop: horizontal scroll needs more games than the visible area.
				const tile_width = this.default_tile_width || 150;
				const scroll_rows = this.default_scroll_rows || 1;
				// Approximate container width (~95% of viewport for typical layouts)
				const container_width = Math.floor(width * 0.95);
				const visible_cols = Math.max(1, Math.floor((container_width + GAP) / (tile_width + GAP)));
				this.landing_games_per_page = visible_cols * scroll_rows * 2;
			}

			// General pages: keep breakpoint table for standard pagination
			const breakpoints = [
				{ min: 0, max: 539, general: 11 },
				{ min: 540, max: 670, general: 18 },
				{ min: 671, max: 991, general: 15 },
				{ min: 992, max: 1100, general: 22 },
				{ min: 1101, max: 1359, general: 37 },
				{ min: 1360, max: 1599, general: 37 },
				{ min: 1600, max: Infinity, general: 37 },
			];
			const config = breakpoints.find(bp => width >= bp.min && width <= bp.max);
			this.general_games_per_page = config ? config.general : 37;
		},
		resetCasinoParameters() {
			this.current_page = 1;
		},
		toggleFavourite(game_id) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/favourite/slot', { id: game_id }).then((response) => {
				useCustomerStore().customer.favourites = response.data.data;

				let favourites = (useCustomerStore().customer && useCustomerStore().customer.favourites.slots.length > 0) ? useCustomerStore().customer.favourites.slots : null;

				if (this.active_page == 'landing') {
					for (let i = 0; i < Object.keys(this.games).length; i++) {
						const category_games = Object.values(this.games)[i];

						for (let j = 0; j < Object.keys(category_games).length; j++) {
							let favourite = _.find(favourites, { 'game_id': String(category_games[j].id) });

							category_games[j].is_favourite = favourite ? true : false;
							category_games[j].favourite_timestamp = favourite ? Math.floor(Date.parse(favourite.created_at) / 1000) : null;
						}
					}

				} else {
					for (let i = 0; i < Object.keys(this.games).length; i++) {
						let table = Object.values(this.games)[i];
						let favourite = _.find(favourites, { 'game_id': String(table.id) });

						table.is_favourite = favourite ? true : false;
						table.favourite_timestamp = favourite ? Math.floor(Date.parse(favourite.created_at) / 1000) : null;

						if (this.active_page == 'favourites') {
							if (!table.is_favourite) {
								// Remove the table from the list if it's not a favourite
								this.games.splice(i, 1);
								i--;
							}
						}
					}
				}

				if (this.active_page == 'game' || this.active_page == 'landing') {
					// Update favourites in all loaded collections
					Object.keys(this.collections).forEach(collection_slug => {
						const collection = this.collections[collection_slug];

						for (let i = 0; i < collection.length; i++) {
							let game = collection[i];
							let favourite = _.find(favourites, { 'game_id': String(game.id) });

							game.is_favourite = favourite ? true : false;
							game.favourite_timestamp = favourite ? Math.floor(Date.parse(favourite.created_at) / 1000) : null;
						}
					});
				}

				this.has_favourites = favourites && favourites.length > 0;

				return Promise.resolve(this.has_favourites);
			}).catch((error) => {
				return Promise.reject(error);
			})
		},
		setRedirectToLanding(value) {
			this.redirect_to_landing = value;
		},
		setLandingPageType(landing_page_type) {
			this.landing_page_type = landing_page_type;
			this.current_page = 1;
		},
		getRouteByPage(active_page, category_slug, provider_slugs = []) {
			const has_providers = Array.isArray(provider_slugs) && provider_slugs.length > 0;
			const providers_string = provider_slugs.join(',');
			const params = {};

			// Always add custom_page_slug if exists and needed
			if (this.custom_page_slug) {
				params.custom_page_slug = this.custom_page_slug;
			}

			// Helper to build route object
			const route = (name, extra = {}) => ({
				name,
				params: { ...params, ...extra }
			});

			switch (active_page) {
				case 'landing':
				case 'provider':
				case 'custom-provider': {
					if (has_providers) {
						return route(this.custom_page_slug ? 'custom-casino-providers' : 'casino-providers', { provider_slug: providers_string });
					}
					return route(this.custom_page_slug ? 'custom-casino' : 'casino');
				}

				case 'favourites':
				case 'custom-favourites':
				case 'casino-favourites-provider':
				case 'custom-casino-favourites-provider': {
					if (has_providers) {
						return route(this.custom_page_slug ? 'custom-casino-favourites-provider' : 'casino-favourites-provider', { provider_slug: providers_string });
					}
					return route(this.custom_page_slug ? 'custom-casino-favourites' : 'casino-favourites');
				}

				case 'last_played_games':
				case 'custom-last-played-games': {
					if (has_providers) {
						return route(this.custom_page_slug ? 'custom-casino-last-played-provider' : 'casino-last-played-provider', { provider_slug: providers_string });
					}
					return route(this.custom_page_slug ? 'custom-casino-last-played' : 'casino-last-played');
				}

				default: {
					// Category & providers
					if (has_providers && category_slug) {
						return route(this.custom_page_slug ? 'custom-casino-category-provider' : 'casino-category-provider', {
							category_slug,
							provider_slug: providers_string
						});
					}

					// Providers only
					if (has_providers) {
						return route(this.custom_page_slug ? 'custom-casino-providers' : 'casino-providers', {
							provider_slug: providers_string
						});
					}

					// Category only
					if (category_slug) {
						return route(this.custom_page_slug ? 'custom-casino-category' : 'casino-category', {
							category_slug
						});
					}

					// Fallback
					return route(this.custom_page_slug ? 'custom-casino' : 'casino');
				}
			}
		},
		openGame(game_id, demo = false) {
			let protocol = window.location.protocol;
			let hostname = window.location.hostname;
			let port = window.location.port ? window.location.port : '';
			let close_url = protocol + '//' + hostname + (port ? ':' + port : '') + '/casino/close';

			let payload = {
				game_id: game_id,
				close_url: close_url
			};

			if (demo) payload['demo'] = true;

			return global.axios.post(process.env.VUE_APP_API_URL + '/casino/game', payload).then((response) => {

				return Promise.resolve(response.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		claimFreespinsAndPlay(freespin_id, slot_id) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/v2/account/freespins/claim', { freespin_id: freespin_id, slot_id: slot_id }).then(response => {

				return Promise.resolve(response.data);
			}).catch(error => {
				return Promise.reject(error);
			});
		},
		cancelFreespin(freespin_id) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/v2/account/freespins/cancel', { freespin_id: freespin_id }).then(response => {

				return Promise.resolve(response.data);
			}).catch(error => {
				return Promise.reject(error);
			});
		},
	},
})